import React__default, { memo, createContext, forwardRef, useEffect, useImperativeHandle, useContext } from 'react';
import { useWatch, useForm } from '../../../../../node_modules/.pnpm/react-hook-form@7.22.0_react@19.0.0/node_modules/react-hook-form/dist/index.esm.mjs.js';
import { yupResolver } from '@hookform/resolvers/yup';
import useWindow from '../Hooks/useWindow.js';

function FormFieldWatcher({ control, onFormFieldsChange, setValue, isValid, isSubmitted }) {
    const formFields = useWatch({ control });
    useEffect(() => {
        onFormFieldsChange(formFields, setValue, isValid, isSubmitted);
    }, [formFields, isValid, isSubmitted, setValue, onFormFieldsChange]);
    return null;
}
const MemoizedFormFieldWatcher = memo(FormFieldWatcher, (
/* eslint-disable-next-line */
prev, 
/* eslint-disable-next-line */
next) => {
    return (prev.control === next.control &&
        prev.onFormFieldsChange === next.onFormFieldsChange &&
        prev.setValue === next.setValue &&
        prev.isValid === next.isValid &&
        prev.isSubmitted === next.isSubmitted);
});
// eslint-disable-next-line
const FormContext = createContext({});
function Form(props, ref) {
    const window = useWindow();
    const { children, validationSchema, onSubmit, initialValues, onFormFieldsChange, onFormBlur, yupContext, scrollToError, mode = 'onSubmit', reValidateMode = 'onChange', errorCallback, exposeSeparateFieldValue } = props;
    const { register, handleSubmit, control, setValue, getValues, trigger, reset, formState: { errors, isValid, isSubmitted } } = useForm(validationSchema
        ? {
            resolver: yupResolver(validationSchema),
            context: { ...yupContext },
            defaultValues: initialValues,
            mode,
            reValidateMode
        }
        : {
            defaultValues: initialValues,
            mode,
            reValidateMode
        });
    useImperativeHandle(ref, () => ({
        triggerSubmit: () => {
            // eslint-disable-next-line
            return new Promise(async (resolve) => {
                if (await trigger()) {
                    if (onSubmit) {
                        onSubmit(getValues());
                    }
                    resolve(getValues());
                }
                else {
                    resolve({});
                }
            });
        },
        triggerSubmitWithoutValidation: () => {
            // eslint-disable-next-line
            return new Promise(async (resolve) => {
                if (onSubmit) {
                    onSubmit(getValues());
                }
                resolve(getValues());
            });
        },
        triggerFieldValidation: async (fieldName) => {
            return await trigger(fieldName);
        },
        resetForm: (values) => {
            reset({ ...values, id: initialValues.id });
        },
        getCurrentValues: () => getValues(),
        setValue: (name, value) => setValue(name, value)
    }));
    useEffect(() => {
        if (Object.keys(errors).length > 0 && scrollToError) {
            const firstError = window?.document?.querySelectorAll('.form__error__top')[0];
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth' });
            }
            if (errorCallback) {
                errorCallback(errors);
            }
        }
    }, [errors, scrollToError]);
    const onFormSubmit = () => {
        return handleSubmit((values) => {
            if (onSubmit) {
                onSubmit(values);
            }
        });
    };
    return (React__default.createElement(FormContext.Provider, { value: {
            register,
            handleSubmit,
            control,
            errors,
            setValue,
            getValues,
            exposeSeparateFieldValue
        } },
        React__default.createElement("form", { onSubmit: onFormSubmit(), onBlur: onFormBlur }, children),
        onFormFieldsChange ? (React__default.createElement(MemoizedFormFieldWatcher, { setValue: setValue, control: control, isValid: isValid, isSubmitted: isSubmitted, onFormFieldsChange: onFormFieldsChange })) : null));
}
const useFormContext = () => {
    const context = useContext(FormContext);
    if (!context) {
        throw new Error('FormContext not usable outside of context provider');
    }
    return context;
};
var Form$1 = forwardRef(Form);

export { Form$1 as default, useFormContext };
//# sourceMappingURL=Form.js.map
